/**
 * `/app/settings/billing`.
 *
 * Renders one of the states in `billing-state.ts`. Three things this page has
 * to get right, in order of how badly each fails:
 *
 * **Signed out.** Previously not a state at all — a visitor was treated as a
 * free user, shown an upgrade button, and got a 401 for pressing it. Now the
 * plans are visible and the action signs them in and returns them here with
 * their choice intact, so the round trip costs no context.
 *
 * **Arriving from Stripe.** `success_url` fires before the webhook that grants
 * the tier, so the page polls and says *activating* rather than claiming a
 * success it cannot see. A timeout is not a failure; the money moved.
 *
 * **Cancelling.** One button to the portal, and the note says what is behind
 * it so nobody has to click to find out whether leaving is possible.
 */
import { formatBytes } from "../../shared/tiers";
import { api } from "../api";
import { type ActivationState, consumeCheckoutReturn, explainBillingFailure } from "../api/billing";
import { type Identity, identity, onIdentityChange, refresh } from "../auth/session";
import { billingView, hasPortal, headline, showsPlans, usageLine } from "./billing-state";
import { actionButton, el, formatDate, replace } from "./dom";
import { createUpgradePanel } from "./upgrade-panel";

export interface BillingPanel {
  el: HTMLElement;
  destroy(): void;
}

export function createBillingPanel(): BillingPanel {
  const root = el("section", { class: "hud-billing", aria: { label: "Plan and billing" } });
  const status = el("div", {
    class: "hud-billing__status",
    hidden: true,
    aria: { live: "polite" },
  });
  const body = el("div", { class: "hud-billing__body" });
  const abort = new AbortController();

  /**
   * False until `/api/me` has answered once.
   *
   * Without it the page renders from the cached SIGNED_OUT default and then
   * corrects itself — which flashes a sign-in prompt at someone who is signed
   * in, and an upgrade offer at someone who already pays.
   */
  let ready = false;

  replace(root, status, body);

  function setStatus(text: string, tone = "info") {
    status.textContent = text;
    status.dataset.tone = tone;
    status.hidden = text === "";
  }

  async function openPortal() {
    setStatus("");
    try {
      window.location.assign(await api.billing.openPortal());
    } catch (err) {
      setStatus(explainBillingFailure(err), "error");
    }
  }

  function render(id: Identity) {
    const view = billingView(id, ready);

    if (view.kind === "loading") {
      // A quiet placeholder, not a spinner. This resolves in one round trip and
      // a spinner would flash.
      replace(body, el("div", { class: "hud-billing__loading", aria: { busy: "true" } }));
      return;
    }

    const { title, detail, urgent } = headline(view, formatDate);

    replace(
      body,
      el(
        "header",
        { class: "hud-billing__plan" },
        el("h2", { class: "hud-billing__tier", text: title }),
        // Where you are, then what more would buy. Reversing these two makes
        // the page open with a pitch before it has said anything true.
        usage,
        el("p", {
          class: "hud-billing__detail",
          data: { urgent: urgent ? "true" : null },
          text: detail,
        }),
      ),

      // Plans, when there is something to buy. The panel handles signed-out
      // itself, so this does not branch again.
      showsPlans(view) ? createUpgradePanel().el : null,

      hasPortal(view)
        ? el(
            "div",
            { class: "hud-billing__manage-block" },
            actionButton("Manage billing", openPortal, { class: "hud-billing__manage" }),
            el("p", {
              class: "hud-billing__note",
              // Names what is behind the button. Nobody should have to click it
              // to discover whether cancelling is possible.
              text: "Change plan, update your card, download invoices, or cancel. Cancelling keeps your access until the end of the period you have paid for.",
            }),
          )
        : null,
    );

    if (view.kind !== "signed-out") void loadUsage(id.tier);
  }

  /**
   * One line, with the quota in it. A number nobody can compare to anything is
   * not information, and an empty element is better than a label reading zero.
   */
  const usage = el("p", { class: "hud-billing__usage", hidden: true });

  async function loadUsage(tier: Parameters<typeof usageLine>[0]) {
    try {
      const account = await api.account.getAccount(abort.signal);
      usage.textContent = usageLine(tier, account.projectCount, account.storageBytes, formatBytes);
      usage.hidden = false;
    } catch {
      // Supporting detail. Failing to load it must not take the page with it.
      usage.hidden = true;
    }
  }

  function onActivation(state: ActivationState) {
    if (state.status === "activating") return setStatus("Activating your plan…", "busy");
    if (state.status === "active") return setStatus("Your plan is active.", "ok");
    // Not a failure. The payment went through and the webhook is late; saying
    // so beats an error nobody can act on, and beats a false success by more.
    setStatus(
      "Your payment went through. The plan is taking a moment to activate — refresh in a few seconds.",
      "warn",
    );
  }

  const stop = onIdentityChange((id) => {
    render(id);
  });

  const outcome = consumeCheckoutReturn();
  if (outcome === "success") {
    setStatus("Activating your plan…", "busy");
    void api.billing.awaitEntitlement(onActivation, { signal: abort.signal });
  } else if (outcome === "cancelled") {
    // Abandoning checkout is normal and gets no scolding.
    setStatus("No changes made.");
  }

  // One read, then render for real. `onIdentityChange` already fired once with
  // the cached value, which is why `ready` gates the first paint.
  void refresh().then(() => {
    ready = true;
    render(identity());
  });

  return {
    el: root,
    destroy() {
      stop();
      abort.abort();
      root.remove();
    },
  };
}
